import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { ExternalLink, LinkRemove } from '@factorialco/f0-react/icons/app'
import {
  JobLevelEmployeesDataCollectionDocument,
  JobLevelEmployeesDataCollectionItemFragment,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import EmployeeSidepanel from 'modules/project_management/components/EmployeeSidepanel'
import { SelectedEmployeeInfo } from 'modules/project_management/components/tables/JobLevelEmployeesDataCollection/types'
import { getPeopleCount } from 'modules/project_management/lib/constants'

interface JobLevelEmployeesDataCollectionProps {
  canManageRates: boolean
  referenceRateId: number
}

const JobLevelEmployeesDataCollection = ({
  canManageRates,
  referenceRateId,
}: JobLevelEmployeesDataCollectionProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const [showEmployeeSidepanel, setShowEmployeeSidepanel] = useState(false)
  const [selectedEmployeeInfo, setSelectedEmployeeInfo] = useState<SelectedEmployeeInfo>({
    avatar: '',
    id: 0,
    firstName: '',
    lastName: '',
    billingRateCents: 0,
    costRate: 0,
    profitabilityPercentageCents: 0,
    currency: '',
  })
  const {
    access: { locale },
  } = useCurrentSession()

  const itemOnClick = (item: JobLevelEmployeesDataCollectionItemFragment) => () => {
    setShowEmployeeSidepanel(true)
    setSelectedEmployeeInfo({
      avatar: item.resourceEmployee?.access?.avatar ?? '',
      id: item.resourceEmployee?.id ?? 0,
      firstName: item.resourceEmployee?.firstName ?? '',
      lastName: item.resourceEmployee?.lastName ?? '',
      billingRateCents: item.billingRateCents,
      costRate: item.chargingCostCents,
      profitabilityPercentageCents: item.profitabilityPercentageCents,
      currency: item.currency,
    })
  }

  const source = useGraphqlDataCollectionSource({
    document: JobLevelEmployeesDataCollectionDocument,
    totalItemSummary: (totalItems) => getPeopleCount(totalItems),
    mapData: (data) => {
      return data.projectManagement.ratesConnection.nodes ?? []
    },
    mapVariables: () => ({ referenceRateId }),
    mapPagination: (data) => {
      return {
        total: data.projectManagement.ratesConnection.totalCount ?? 0,
      }
    },
    itemOnClick: canManageRates ? itemOnClick : undefined,
  })

  return (
    <>
      <OneDataCollection
        source={source}
        emptyStates={{
          'no-data': {
            title: i18n.t('project_management.job_level_employees.table.empty_state.title'),
            description: i18n.t(
              'project_management.job_level_employees.table.empty_state.description'
            ),
            emoji: '🎯',
            actions: [
              {
                label: i18n.t(
                  'project_management.job_level_employees.table.empty_state.button_text'
                ),
                icon: ExternalLink,
                variant: 'outline',
                onClick: () => {
                  navigate(navigation.employees.list.fullPath())
                },
              },
            ],
          },
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: compact([
                {
                  label: i18n.t(
                    'project_management.job_level_employees.table.headers.person.label'
                  ),
                  render: (item: JobLevelEmployeesDataCollectionItemFragment) => {
                    return {
                      type: 'person',
                      value: {
                        firstName: item?.resourceEmployee?.firstName,
                        lastName: item?.resourceEmployee?.lastName,
                        src: item?.resourceEmployee?.access?.avatar
                          ? buildUrl(item?.resourceEmployee?.access?.avatar)
                          : undefined,
                      },
                    }
                  },
                },
                {
                  label: i18n.t(
                    'project_management.job_level_employees.table.headers.cost_rate.label'
                  ),
                  info: i18n.t(
                    'project_management.job_level_employees.table.headers.cost_rate.tooltip_message'
                  ),
                  align: 'right',
                  render: (item: JobLevelEmployeesDataCollectionItemFragment) => {
                    return {
                      type: 'text',
                      value: MoneyWithCurrency({
                        amount: item?.chargingCostCents,
                        currency: item?.currency,
                        optionalDecimals: true,
                        locale,
                      }),
                    }
                  },
                },
                {
                  label: i18n.t(
                    'project_management.job_level_employees.table.headers.bill_rate.label'
                  ),
                  info: i18n.t(
                    'project_management.job_level_employees.table.headers.bill_rate.tooltip_message'
                  ),
                  align: 'right',
                  render: (item: JobLevelEmployeesDataCollectionItemFragment) => {
                    return {
                      type: 'text',
                      value: MoneyWithCurrency({
                        amount: item?.billingRateCents,
                        currency: item?.currency,
                        optionalDecimals: true,
                        locale,
                      }),
                    }
                  },
                },
                {
                  label: i18n.t(
                    'project_management.job_level_employees.table.headers.profit_margin.label'
                  ),
                  info: i18n.t(
                    'project_management.job_level_employees.table.headers.profit_margin.tooltip_message'
                  ),
                  align: 'right',
                  render: (item: JobLevelEmployeesDataCollectionItemFragment) => {
                    return {
                      type: 'text',
                      value: `${item?.profitabilityPercentageCents / 100}%`,
                    }
                  },
                },
                {
                  label: i18n.t(
                    'project_management.job_level_employees.table.headers.custom_rate.label'
                  ),
                  info: i18n.t(
                    'project_management.job_level_employees.table.headers.custom_rate.tooltip_message'
                  ),
                  render: (item: JobLevelEmployeesDataCollectionItemFragment) => {
                    if (item?.unlinked) {
                      return {
                        type: 'tag',
                        value: {
                          label: i18n.t('project_management.job_level_employees.table.custom'),
                          icon: LinkRemove,
                        },
                      }
                    }
                  },
                },
              ]),
            },
          },
        ]}
      />
      {showEmployeeSidepanel && (
        <EmployeeSidepanel
          refetchQueries={[JobLevelEmployeesDataCollectionDocument]}
          showSidePanel={showEmployeeSidepanel}
          onClose={() => setShowEmployeeSidepanel(false)}
          selectedEmployeeInfo={selectedEmployeeInfo}
        />
      )}
    </>
  )
}

export default JobLevelEmployeesDataCollection
